import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const testimonials = [
  {
    id: 1,
    content:
      "Square Trigger delivered a structured website redesign and helped improve our digital visibility. Their process was professional and results-focused.",
    author: "Einsured.ca",
  },
  {
    id: 2,
    content:
      "The custom tools and SEO improvements helped streamline our operations and strengthen our online presence.",
    author: "Canadardsp.com",
  },
];

export const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const nextTestimonial = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setDirection(-1);
    setCurrentIndex(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 7000);

    return () => clearInterval(timer);
  }, []);

  const current = testimonials[currentIndex];

  const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? 220 : -220, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir < 0 ? 220 : -220, opacity: 0 }),
  };

  return (
    <section className="pt-8 sm:pt-10 lg:pt-12 pb-12 sm:pb-14 lg:pb-16 bg-muted/30 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 right-10 w-72 h-72 bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-10 sm:mb-14"
        >
          <span className="text-sm font-medium text-primary uppercase tracking-wider">
            TESTIMONIALS
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mt-3 mb-4 sm:mb-6">
            What Our <span className="gradient-text">Clients Say</span>
          </h2>
        </motion.div>

        {/* Carousel */}
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Quote Icon */}
            <div className="absolute -top-7 left-4 sm:left-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full gradient-primary-soft flex items-center justify-center">
              <Quote className="h-7 w-7 sm:h-8 sm:w-8 text-primary" />
            </div>

            {/* Card */}
            <div className="bg-card rounded-2xl border border-border p-6 sm:p-8 md:p-12 shadow-soft min-h-[240px] sm:min-h-[260px] flex items-center">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={currentIndex}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.45 }}
                  className="w-full text-center sm:text-left"
                >
                  <p className="text-base sm:text-lg md:text-xl text-foreground leading-relaxed">
                    “{current.content}”
                  </p>

                  <div className="mt-6 sm:mt-8 text-foreground/80 font-semibold text-center sm:text-left">
                    — {current.author}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Nav */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-6 sm:mt-8">
              {/* Dots */}
              <div className="flex justify-center sm:justify-start gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setDirection(index > currentIndex ? 1 : -1);
                      setCurrentIndex(index);
                    }}
                    aria-label={`Go to testimonial ${index + 1}`}
                    className={`h-2.5 rounded-full transition-all duration-300 ${
                      index === currentIndex
                        ? "w-8 gradient-primary"
                        : "w-2.5 bg-muted hover:bg-muted-foreground/30"
                    }`}
                  />
                ))}
              </div>

              {/* Arrows */}
              <div className="flex justify-center sm:justify-end gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={prevTestimonial}
                  className="h-11 w-11 sm:h-12 sm:w-12 rounded-full"
                  aria-label="Previous testimonial"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={nextTestimonial}
                  className="h-11 w-11 sm:h-12 sm:w-12 rounded-full"
                  aria-label="Next testimonial"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};